import { CustomUnitFields, UnitSystem } from 'providers/api';

const volumeConversionFactor = 0.02832;
const distanceConversionFactor = 0.3048;
const tonnageConversionFactor = 0.907185;
const densityConversionFactor = 0.06242796;
const pressureConversionFactor = 6.895;
const pumpPressureConversionFactor = 0.06895;
const gravityConversionFactor = 3.28084;
const kpaPerMeterHead = 9.80665;
const smallDistanceSlumpConversionFactor = 25.4;
const flowRateConversionFactor = 4.4028;
const binderTonnesConversionFactor = 1.10231;
const powerConversionFactor = 1.341;
const speedConversionFactor = 0.3048;
const lengthConversionFactor = 0.3937;
const weightConversionFactor = 0.45454;
const smallWeightConversionFactor = 0.035274;
const largeWeightConversionFactor = 1.10231;
const millimetresPerMetre = 1000;

// Volume (Stope, Pour)
// - cubic feet (ft^3)(imperial)
// - cubic metres (m^3)(metric)
export const volumeImperialToMetric = (ft: number) => volumeConversionFactor * ft;
export const volumeMetricToImperial = (m: number) => m / volumeConversionFactor;

// Distance
// - feet (ft)(imperial)
// - metres (m)(metric)
export const distanceImperialToMetric = (ft: number) => distanceConversionFactor * ft;
export const distanceMetricToImperial = (d: number) => d / distanceConversionFactor;

// length
// - inch (in)(imperial)
// - centimetres (cm)(metric)
export const lengthImperialToMetric = (inches: number) => inches / lengthConversionFactor;
export const lengthMetricToImperial = (cm: number) => cm * lengthConversionFactor;

// Tonnage (Dry solids feed rate, slurry tonnage)
// - short tons per hour (tn/h)(imperial)
// - tonnes per hour (t/h)(metric)
export const tonnageImperialToMetric = (tn: number) => tonnageConversionFactor * tn;
export const tonnageMetricToImperial = (t: number) => t / tonnageConversionFactor;

// Density
// - short ton per cubic feet (lb/ft^3)(imperial)
// - kilograms per cubic metre (kg/m^3)(metric)
export const densityImperialToMetric = (lbPft: number) => lbPft / densityConversionFactor;
export const densityMetricToImperial = (kgPm: number) => kgPm * densityConversionFactor;

// Pressure (UCS)
// - pounds per square inch (psi)(imperial)
// - kilopascals (kPa)(metric)
export const pressureImperialToMetric = (psi: number) => pressureConversionFactor * psi;
export const pressureMetricToImperial = (kPa: number) => kPa / pressureConversionFactor;

// Pressure (Pump)
// - pounds per square inch (psi)(imperial)
// - bar (bar)(metric)
export const pumpPressureImperialToMetric = (psi: number) => pumpPressureConversionFactor * psi;
export const pumpPressureMetricToImperial = (bar: number) => bar / pumpPressureConversionFactor;
export const pumpPressureKpaToBar = (kpa: number) => kpa / 100;
export const pumpPressureBarToKpa = (bar: number) => bar * 100;
export const pumpPressureBarToGravityMetricHead = (bar: number) => (bar * 100) / kpaPerMeterHead;
export const gravityMetricHeadToPumpPressureBar = (mHead: number) => (mHead * kpaPerMeterHead) / 100;

// Gravity (Pump)
// - meter head (m head)(metric)
// - feet head (ft)(imperial)
export const gravityImperialToMetric = (ft: number) => ft / gravityConversionFactor;
export const gravityMetricToImperial = (m: number) => m * gravityConversionFactor;

// small distances (slump)
// - inches (in)(imperial)
// - milimetres (mm)(metric)
export const smallDistanceImperialToMetric = (inch: number) => smallDistanceSlumpConversionFactor * inch;
export const smallDistanceMetricToImperial = (mm: number) => mm / smallDistanceSlumpConversionFactor;
export const metresToMillimetres = (metres: number) => metres * millimetresPerMetre;
export const millimetresToMetres = (millimetres: number) => millimetres / millimetresPerMetre;

// Flow Rate
// - cubic feet per second (gpm)(imperial)
// - cubic metres per hour (m^3/h)(metric))
export const flowRateImperialToMetric = (gPm: number) => gPm / flowRateConversionFactor;
export const flowRateMetricToImperial = (mPh: number) => mPh * flowRateConversionFactor;

// Binder Tonnes
// - metric ton per hour (tnPerHour)(imperial)
// - imperial ton per hour (tPerHour)(metric))
export const binderTonnesImperialToMetric = (tnPerHour: number) => tnPerHour / binderTonnesConversionFactor;
export const binderTonnesMetricToImperial = (tPerHour: number) => tPerHour * binderTonnesConversionFactor;

// Power (mixer)
// - horsepower (hp)(imperial)
// - kilowatts (kW)(metric)
export const powerImperialToMetric = (hp: number) => powerConversionFactor * hp;
export const powerMetricToImperial = (kW: number) => kW / powerConversionFactor;

// Speed (in pipe)
// - feet per second (ft/s)(imperial)
// - metres per second (m/s)(metric)
export const speedImperialToMetric = (ftPs: number) => speedConversionFactor * ftPs;
export const speedMetricToImperial = (mPs: number) => mPs / speedConversionFactor;

// Friction Loss (pipe)
// - psi/ft (imperial)
// - kPa/m (metric)
// kPa/m -> psi/ft: divide by pressureConversionFactor (kPa->psi), multiply by distanceConversionFactor (m->ft)
export const frictionLossMetricToImperial = (kPaPerM: number) => (kPaPerM / pressureConversionFactor) * distanceConversionFactor;
export const frictionLossImperialToMetric = (psiPerFt: number) => (psiPerFt * pressureConversionFactor) / distanceConversionFactor;

// weight
// - pounds (lbs)(imperial)
// - kilograms (kg)(metric)
export const weightImperialToMetric = (lbs: number) => lbs * weightConversionFactor;
export const weightMetricToImperial = (kg: number) => kg / weightConversionFactor;

// Small Weight
// - ounces (oz)(imperial)
// - grams (g)(metric)
export const smallWeightImperialToMetric = (ounces: number) => ounces / smallWeightConversionFactor;
export const smallWeightMetricToImperial = (grams: number) => grams * smallWeightConversionFactor;

// Diameter (in pipe)
// - inches per second (in)(imperial)
// - milimetres per second (mm)(metric)
export const diameterImperialToMetric = smallDistanceImperialToMetric;
export const diameterMetricToImperial = smallDistanceMetricToImperial;

// Large Weight
// - metric tonnes (t)(metric)
// - imperial tonnes (tn)(imperial)
export const largeWeightImperialToMetric = (tn: number) => tn / largeWeightConversionFactor;
export const largeWeightMetricToImperial = (t: number) => t * largeWeightConversionFactor;

// output used to compare front and backend parity
export const run = () => {
  const testValue = 1234;
  /* eslint no-console: */
  console.log({ volumeImperialToMetric: volumeImperialToMetric(testValue) });
  console.log({ volumeMetricToImperial: volumeMetricToImperial(testValue) });
  console.log({ distanceImperialToMetric: volumeMetricToImperial(testValue) });
  console.log({ distanceMetricToImperial: distanceMetricToImperial(testValue) });
  console.log({ tonnageImperialToMetric: tonnageImperialToMetric(testValue) });
  console.log({ tonnageMetricToImperial: tonnageMetricToImperial(testValue) });
  console.log({ densityImperialToMetric: densityImperialToMetric(testValue) });
  console.log({ densityMetricToImperial: densityMetricToImperial(testValue) });
  console.log({ pressureImperialToMetric: pressureImperialToMetric(testValue) });
  console.log({ pressureMetricToImperial: pressureMetricToImperial(testValue) });
  console.log({ pumpPressureImperialToMetric: pumpPressureImperialToMetric(testValue) });
  console.log({ pumpPressureMetricToImperial: pumpPressureMetricToImperial(testValue) });
  console.log({ smlDistImperialToMetric: smallDistanceImperialToMetric(testValue) });
  console.log({ smlDistMetricToImperial: smallDistanceMetricToImperial(testValue) });
  console.log({ flowrateImperialToMetric: flowRateImperialToMetric(testValue) });
  console.log({ flowrateMetricToImperial: flowRateMetricToImperial(testValue) });
  console.log({ powerImperialToMetric: powerImperialToMetric(testValue) });
  console.log({ powerMetricToImperial: powerMetricToImperial(testValue) });
};

// Mirrors the server's PourNoteModelViewGenerator.IsCustomUnitField: the project's customUnitFields
// list INVERTS the base unit system for each listed field. A unit with no gateable field
// (field undefined, e.g. Weight or Percentage) always follows the base system.
export const isCustomUnitField = (unitSystemPreference: UnitSystem, customUnitFields: CustomUnitFields[], field?: CustomUnitFields) => {
  if (!customUnitFields || customUnitFields.length === 0) {
    return unitSystemPreference !== UnitSystem.Metric;
  }

  const isInverted = field !== undefined && customUnitFields.includes(field);
  return unitSystemPreference === UnitSystem.Metric ? isInverted : !isInverted;
};
